import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import useAuth from '../../auth/useAuth';

import Layout from '../UI/introGameLayout/layout';
import ElementsTable from '../UI/elementsTable';

import {
  generalInfoSummaryQuery,
  consolidatedInsightsQuery,
} from './api';

import {
  popupMessagesSummaryDecisionBreakdown,
  summaryDecisionBreakdownElementsHeaders,
  summaryDecisionBreakdownAllOtherAccountsHeaders,
  summaryDecisionBreakdownElementQuestions
} from './data';

import PopupMessages from '../UI/introGameLayout/popupMessages';

import './styles.css';

export default function NormalBalanceSummaryDecisionBreakdown() {
  const navigate = useNavigate();

  const {
    getUser,
    getCurrentNormalGameId
  } = useAuth();

  const user = getUser();

  const gameId = getCurrentNormalGameId();

  const [isFirstGameOfTheType, setIsFirstGameOfTheType] = useState(false);

  const [elementsTableData, setElementsTableData] = useState([]);
  const [allOtherAccountsTableData, setAllOtherAccountsTableData] = useState([]);
  const [dataLoaded, setDataLoaded] = useState(false);

  const { data: generalInfo } = useQuery(generalInfoSummaryQuery()) || {};
  const { data: consolidatedInsights } = useQuery({
    ...consolidatedInsightsQuery({ userId: user?.id }),
    enabled: !!user?.id,
  });

  const games = generalInfo?.games;
  const gamesSessions = generalInfo?.sessions;

  useEffect(() => {
    if(gamesSessions && games && gameId && user) {
      if(gamesSessions.filter(
          session =>
            games.find(game => game.id === session.game && game.game_type === 'Normal Balance')
        ).length < 2) {
        setIsFirstGameOfTheType(true);
      }
    }
  }, [gamesSessions, games, gameId, user]);

  useEffect(() => {
    if(consolidatedInsights) {
      const normalInsights = consolidatedInsights.normal_balance || [];

      const elementsData = [];
      const allOtherAccountsData = [];

      normalInsights.forEach(insight => {
        const row = {
          question: insight.question,
        };

        (insight.options || []).forEach(option => {
          row[option.option] = {
            value: option.count_users,
            isParentMatch: option.is_correct,
            background: option.count_users
              ? option.is_correct
                ? '#D1E9D6'
                : '#FD9595'
              : '#FFFFFF',
          };
        });

        if (summaryDecisionBreakdownElementQuestions.includes(row.question)) {
          elementsData.push(row);
        } else {
          allOtherAccountsData.push(row);
        }
      });

      setElementsTableData(elementsData);
      setAllOtherAccountsTableData(allOtherAccountsData);
      setDataLoaded(true);
    }
  }, [consolidatedInsights]);

  const handlePopupMessagesNextButtonClick = () => {
    let redirectPath = '/normal/summary/final';
    if(isFirstGameOfTheType) {
      redirectPath = '/normal/summary/feedback';
    }
    navigate(redirectPath);
  }

  const handlePopupMessagesPrevButtonClick = () => {
    navigate('/normal/summary/breakdown');
  }

  return(
    <Layout
      backgroundColor="#FBEDD5"
      isBackgroundBlurred={false}
      isHeaderVisible={true}
      isFooterVisible={false}
    >
      {
        dataLoaded &&
          <div className="game-summary-container game-summary-decision-breakdown-container">
            <PopupMessages
              messages={popupMessagesSummaryDecisionBreakdown}
              messageSize="small"
              buttonText="Next"
              onButtonClick={handlePopupMessagesNextButtonClick}
              additionalButtonText="Back"
              onAdditionalButtonClick={handlePopupMessagesPrevButtonClick}
            />
            <div className="summary-decision-breakdown-tables">
              <div className="summary-decision-breakdown-table-container">
                <ElementsTable
                  title="The Elements"
                  tableColumnHeader="Sorting Items"
                  tableHeaders={summaryDecisionBreakdownElementsHeaders}
                  tableData={elementsTableData}
                />
              </div>

              {
                allOtherAccountsTableData.length > 0 &&
                  <div className="summary-decision-breakdown-table-container">
                    <ElementsTable
                      title="All other Accounts (incl. the Tricky Accounts)"
                      tableColumnHeader="Sorting Items"
                      tableHeaders={summaryDecisionBreakdownAllOtherAccountsHeaders}
                      tableData={allOtherAccountsTableData}
                    />
                  </div>
              }
            </div>
          </div>
      }
    </Layout>
  );
}
